import { useCallback, useEffect, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Trash2 } from "lucide-react";

const MODE_LABEL = { cash: "Cash", bank_transfer: "Bank transfer", cheque: "Cheque", card: "Card", online: "Online", unspecified: "Unspecified" };
const todayStr = () => new Date().toISOString().slice(0, 10);

export function PaymentDialog({ order, onClose, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ amount: "", date: todayStr(), mode: "bank_transfer", reference: "", notes: "" });

  const load = useCallback(() => api.get(`/admin/orders/${order.id}/payments`).then((r) => {
    setData(r.data);
    setForm((f) => ({ ...f, amount: r.data.balance_due > 0 ? String(r.data.balance_due) : "" }));
  }), [order]);
  useEffect(() => { if (order) load(); }, [order, load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/orders/${order.id}/payments`, { ...form, amount: Number(form.amount) });
      toast.success("Receipt posted");
      setForm({ amount: "", date: todayStr(), mode: "bank_transfer", reference: "", notes: "" });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reverse = async (pid) => {
    if (!window.confirm("Reverse this receipt? The invoice balance will reopen.")) return;
    try {
      await api.delete(`/admin/payments/${pid}`);
      toast.success("Receipt reversed");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="payment-dialog">
        <DialogHeader><DialogTitle>Receipts · {order?.invoice_no}</DialogTitle></DialogHeader>
        {!data ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm rounded-lg bg-secondary/50 p-3">
              <div><p className="text-xs text-muted-foreground">Invoice</p><p className="font-medium">{money(data.total)}</p></div>
              <div><p className="text-xs text-muted-foreground">Received{data.credits_applied > 0 ? " + credits" : ""}</p><p className="font-medium">{money(data.amount_paid + data.credits_applied)}</p></div>
              <div><p className="text-xs text-muted-foreground">Balance due</p><p className="font-display font-bold text-base" data-testid="payment-balance-due">{money(data.balance_due)}</p></div>
            </div>

            {data.payments.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Payment history</Label>
                {data.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border border-border/60 rounded-md px-3 py-2" data-testid={`payment-row-${p.id}`}>
                    <div>
                      <span className="font-medium">{money(p.amount)}</span>
                      <span className="text-muted-foreground"> · {p.date} · {MODE_LABEL[p.mode] || p.mode}{p.reference ? ` · ${p.reference}` : ""}</span>
                    </div>
                    {user?.role === "admin" && (
                      <button className="text-muted-foreground hover:text-destructive" onClick={() => reverse(p.id)} data-testid={`reverse-payment-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {data.balance_due > 0 ? (
              <div className="space-y-3 border-t border-border pt-3">
                <Label className="text-xs">Record a receipt</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Fld label="Amount (AED)"><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" /></Fld>
                  <Fld label="Received on"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date-input" /></Fld>
                  <Fld label="Mode">
                    <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                      <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{data.modes.map((m) => <SelectItem key={m} value={m}>{MODE_LABEL[m] || m}</SelectItem>)}</SelectContent>
                    </Select>
                  </Fld>
                  <Fld label="Reference (cheque / txn no.)"><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="payment-reference-input" /></Fld>
                </div>
                <p className="text-xs text-muted-foreground">Commission and the new-customer bonus are credited to the month in which the invoice is settled in full.</p>
              </div>
            ) : <Badge className="capitalize">Settled {data.payment_status === "paid" ? "in full" : ""}</Badge>}
          </div>
        )}
        <DialogFooter>
          {data?.balance_due > 0 && (
            <Button onClick={save} disabled={saving || !form.amount} data-testid="save-payment-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post receipt"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}
